/**
 * DirectorBrief — displays the content brief and creative direction
 * produced by the AI directors for a given video.
 * Uses TubeForge design tokens (--tf-* CSS vars).
 */
import { useState } from "react";
import { TfCard, Icon } from "@/components/v2/primitives.tsx";

interface ContentBrief {
  topic?: string;
  angle?: string;
  targetViewer?: string;
  coreMessage?: string;
  emotionalHook?: string;
  hookLine?: string;
  narrativeArc?: string;
  ctaStrategy?: string;
  retentionBeats?: string[];
  toneGuide?: string;
  visualThemes?: string[];
  keyMoments?: string[];
  avoidThemes?: string[];
  episodeTitle?: string;
  publishOrder?: number;
}

interface CreativeDirection {
  visualMood?: string;
  colorPalette?: string;
  lightingStyle?: string;
  compositionNotes?: string;
  recurringMotifs?: string[];
  segmentDirections?: string[];
  visualAvoids?: string[];
}

interface Props {
  contentBrief?: ContentBrief | null;
  creativeDirection?: CreativeDirection | null;
}

export function DirectorBrief({ contentBrief, creativeDirection }: Props) {
  const [openSection, setOpenSection] = useState<"brief" | "creative" | null>(null);

  const hasBrief = contentBrief && Object.keys(contentBrief).length > 0;
  const hasCreative = creativeDirection && Object.keys(creativeDirection).length > 0;

  if (!hasBrief && !hasCreative) return null;

  const toggle = (s: "brief" | "creative") =>
    setOpenSection((prev) => (prev === s ? null : s));

  return (
    <TfCard padding={0}>
      {/* Header */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--tf-rule)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "var(--tf-accent-soft)",
        }}
      >
        <Icon name="sparkle" size={16} color="var(--tf-accent)" />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>AI Director Strategy</div>
          <div className="tf-muted" style={{ fontSize: 11, marginTop: 1 }}>
            How the AI planned this video
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {hasBrief && <Chip>Content Brief</Chip>}
          {hasCreative && <Chip muted>Creative Direction</Chip>}
        </div>
      </div>

      {/* Content Brief */}
      {hasBrief && (
        <Section
          icon="📋"
          title="Content Brief"
          subtitle={contentBrief.topic || "Strategic direction"}
          open={openSection === "brief"}
          onToggle={() => toggle("brief")}
        >
          <FieldGrid
            fields={[
              contentBrief.topic && { label: "Topic", value: contentBrief.topic },
              contentBrief.angle && { label: "Angle", value: contentBrief.angle },
              contentBrief.targetViewer && { label: "Target Viewer", value: contentBrief.targetViewer },
              contentBrief.coreMessage && { label: "Core Message", value: contentBrief.coreMessage },
              contentBrief.emotionalHook && { label: "Emotional Hook", value: contentBrief.emotionalHook },
              contentBrief.hookLine && { label: "Opening Line", value: `"${contentBrief.hookLine}"` },
              contentBrief.narrativeArc && { label: "Narrative Arc", value: contentBrief.narrativeArc },
              contentBrief.ctaStrategy && { label: "CTA Strategy", value: contentBrief.ctaStrategy },
              contentBrief.toneGuide && { label: "Tone", value: contentBrief.toneGuide },
            ].filter(Boolean) as Array<{ label: string; value: string }>}
          />
          {contentBrief.retentionBeats && contentBrief.retentionBeats.length > 0 && (
            <TagRow label="Retention Beats" items={contentBrief.retentionBeats} variant="accent" />
          )}
          {contentBrief.visualThemes && contentBrief.visualThemes.length > 0 && (
            <TagRow label="Visual Themes" items={contentBrief.visualThemes} variant="accent" />
          )}
          {contentBrief.keyMoments && contentBrief.keyMoments.length > 0 && (
            <TagRow label="Key Moments" items={contentBrief.keyMoments} variant="success" />
          )}
          {contentBrief.avoidThemes && contentBrief.avoidThemes.length > 0 && (
            <TagRow label="Avoid" items={contentBrief.avoidThemes} variant="danger" />
          )}
        </Section>
      )}

      {/* Creative Direction */}
      {hasCreative && (
        <Section
          icon="🎨"
          title="Creative Direction"
          subtitle={creativeDirection.visualMood || "Visual direction"}
          open={openSection === "creative"}
          onToggle={() => toggle("creative")}
        >
          <FieldGrid
            fields={[
              creativeDirection.visualMood && { label: "Visual Mood", value: creativeDirection.visualMood },
              creativeDirection.colorPalette && { label: "Color Palette", value: creativeDirection.colorPalette },
              creativeDirection.lightingStyle && { label: "Lighting", value: creativeDirection.lightingStyle },
              creativeDirection.compositionNotes && { label: "Composition", value: creativeDirection.compositionNotes },
            ].filter(Boolean) as Array<{ label: string; value: string }>}
          />
          {creativeDirection.recurringMotifs && creativeDirection.recurringMotifs.length > 0 && (
            <TagRow label="Recurring Motifs" items={creativeDirection.recurringMotifs} variant="accent" />
          )}
          {creativeDirection.visualAvoids && creativeDirection.visualAvoids.length > 0 && (
            <TagRow label="Visual Avoids" items={creativeDirection.visualAvoids} variant="danger" />
          )}
          {creativeDirection.segmentDirections && creativeDirection.segmentDirections.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="tf-label" style={{ marginBottom: 8 }}>Per-Scene Direction</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {creativeDirection.segmentDirections.map((dir, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      padding: "8px 12px",
                      borderRadius: 8,
                      background: "var(--tf-surface)",
                      border: "1px solid var(--tf-rule)",
                    }}
                  >
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        flexShrink: 0,
                        background: "var(--tf-accent-soft)",
                        color: "var(--tf-accent)",
                        display: "grid",
                        placeItems: "center",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {i + 1}
                    </div>
                    <div style={{ fontSize: 12, lineHeight: 1.5 }}>{dir}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>
      )}
    </TfCard>
  );
}

// ── Sub-components ──

function Section({
  icon,
  title,
  subtitle,
  open,
  onToggle,
  children,
}: {
  icon: string;
  title: string;
  subtitle: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={{ borderBottom: "1px solid var(--tf-rule)" }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          padding: "12px 16px",
          background: "none",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 10,
          textAlign: "left",
          color: "var(--tf-fg)",
        }}
      >
        <span style={{ fontSize: 16 }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
          <div
            className="tf-muted"
            style={{
              fontSize: 11,
              marginTop: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </div>
        </div>
        <Icon
          name="chevronDown"
          size={16}
          color="var(--tf-fg-muted)"
        />
      </button>
      {open && (
        <div style={{ padding: "0 16px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function FieldGrid({ fields }: { fields: Array<{ label: string; value: string }> }) {
  if (fields.length === 0) return null;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 10,
        padding: 12,
        borderRadius: 8,
        background: "var(--tf-surface)",
        border: "1px solid var(--tf-rule)",
      }}
    >
      {fields.map((f, i) => (
        <div key={i} style={{ gridColumn: f.value.length > 60 ? "span 2" : undefined }}>
          <div className="tf-label" style={{ fontSize: 10, marginBottom: 3 }}>
            {f.label}
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.5 }}>{f.value}</div>
        </div>
      ))}
    </div>
  );
}

function TagRow({
  label,
  items,
  variant,
}: {
  label: string;
  items: string[];
  variant: "accent" | "success" | "danger";
}) {
  const colors = {
    accent: { bg: "var(--tf-accent-soft)", fg: "var(--tf-accent)", border: "var(--tf-accent)" },
    success: { bg: "var(--tf-accent-soft)", fg: "var(--tf-success)", border: "var(--tf-success)" },
    danger: { bg: "var(--tf-surface-2)", fg: "var(--tf-danger)", border: "var(--tf-danger)" },
  }[variant];

  return (
    <div style={{ marginTop: 4 }}>
      <div className="tf-label" style={{ fontSize: 10, marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {items.map((item, i) => (
          <span
            key={i}
            style={{
              padding: "3px 9px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 500,
              background: colors.bg,
              color: colors.fg,
              border: `1px solid ${colors.border}20`,
            }}
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function Chip({ children, muted }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: 99,
        fontSize: 10,
        fontWeight: 600,
        background: muted ? "var(--tf-surface)" : "var(--tf-accent-soft)",
        color: muted ? "var(--tf-fg-muted)" : "var(--tf-accent)",
        border: `1px solid ${muted ? "var(--tf-rule)" : "var(--tf-accent)"}20`,
      }}
    >
      {children}
    </span>
  );
}
